import { ParsedUrlQuery } from "querystring"

import { endOfToday, startOfToday } from "date-fns"
import {
  GeneralLedgerSortBy as SortBy,
  TransactionType,
} from "gen/accounting/models_pb"
import { SortOrder } from "gen/analyticsv2/models_pb"
import { InternalAccount as ControlAccount } from "gen/coa/models_pb"
import { Location } from "gen/company/models_pb"
import { Timestamp as Timestamppb } from "gen/google/protobuf/timestamp_pb"
import { TimestampRange as TimestampRangePB } from "gen/proto/timestamprange/models_pb"
import { GetGeneralLedgerReq } from "gen/search/service/service_pb"
import { UUID } from "shared-ts/classes/uuid"
import { TimestampRange } from "shared-ts/libs/dates/timestampRange"
import { webUrlToString } from "shared-web/lib/webUrl"

import { LineItem } from "features/admin/reporting/AccountTypeSection"
import {
  useLazySearchalyticsGetGeneralLedger,
  useLazySearchalyticsGetGeneralLedgerCSV,
  useLazySearchalyticsGetGeneralLedgerPDF,
} from "features/api/search"
import { Accounting } from "lib/accounting"
import { deprecatedBuildQueryParams } from "lib/params"

import {
  SerializableReq,
  alphabeticSort,
  numericalSort,
  parseSerializableFilters,
  timeSort,
} from "./helpers"
import { buildEnumFilter, parseEnumValue, parseEnumValues } from "../Filters"
import {
  ACCOUNT_PARAM,
  DATE_RANGE_PARAM,
  LOCATION_PARAM,
  REPORT_PARAM,
  SORT_ORDER_PARAM,
  SORT_PARAM,
  defaultStagedParams,
  joinParameters,
  stagedKey,
} from "../params"
import { ReportT, useReportRes } from "../types"

const GL_TRANSACTION_TYPE_PARAM = "transaction_type"
export const GL_CONTROL_ACCOUNT_PARAM = "internal_accounts"
export const GENERAL_LEDGER_KEY = "general-ledger"
export const GENERAL_LEDGER: ReportT = {
  config: {
    useReportLabels: true,
    paramKey: GENERAL_LEDGER_KEY,
    title: "General ledger",
    dateFilter: {
      type: "daterange",
      pickerType: "daterange",
    },
    filters: {
      location: "Location",
      account: "Account",
      department: "Department",
      method: {
        label: "Method",
        excludeChargeAccount: true,
      },
      fee: "Fee",
    },
    customFilterBuilders: (r) => [
      buildEnumFilter({
        label: "Transaction type",
        paramKey: GL_TRANSACTION_TYPE_PARAM,
        enum: TransactionType,
        names: {
          [TransactionType.UNDEFINED]: "",
          [TransactionType.INTERNAL]: "Internal",
          [TransactionType.EXTERNAL]: "External",
        },
      })(r),
      buildEnumFilter({
        label: "Control account",
        paramKey: GL_CONTROL_ACCOUNT_PARAM,
        names: Accounting.INTERNAL_ACCOUNT_NAMES,
        enum: ControlAccount,
      })(r),
    ],
    sorts: [
      timeSort("Date", SortBy.DATE),
      alphabeticSort("GL Number", SortBy.ACCOUNT_NUMBER),
      alphabeticSort("GL Account", SortBy.ACCOUNT_NAME),
      alphabeticSort("Transaction ID", SortBy.TRANSACTION_ID),
      alphabeticSort("Location ID", SortBy.LOCATION_ID),
      alphabeticSort("Customer ID", SortBy.CUSTOMER_ID),
      numericalSort("Debit", SortBy.DEBIT),
      numericalSort("Credit", SortBy.CREDIT),
      alphabeticSort("Control account", SortBy.CONTROL_ACCOUNT),
      alphabeticSort("Department / Method / Fee", SortBy.DEPARTMENT),
    ],
    defaultParams: () => {
      return {
        [REPORT_PARAM]: GENERAL_LEDGER_KEY,
        [DATE_RANGE_PARAM]: TimestampRange.toQueryParam(
          TimestampRangePB.create({
            start: Timestamppb.fromDate(startOfToday()),
            end: Timestamppb.fromDate(endOfToday()),
          })
        ),
        [SORT_PARAM]: SortBy.DATE.toString(),
        [SORT_ORDER_PARAM]: SortOrder.ASC.toString(),
      }
    },
    downloadType: "csv",
  },
  useReport: useReport,
}

function paramsToReq(p: ParsedUrlQuery): SerializableReq<GetGeneralLedgerReq> {
  return {
    safeFilters: parseSerializableFilters(p, GENERAL_LEDGER.config),
    sortBy: parseEnumValue(p[SORT_PARAM], SortBy, SortBy.UNDEFINED),
    sortOrder: parseEnumValue(p[SORT_ORDER_PARAM], SortOrder, SortOrder.NONE),
    transactionTypes: parseEnumValues(
      p[GL_TRANSACTION_TYPE_PARAM],
      TransactionType
    ),
    controlAccounts: parseEnumValues(
      p[GL_CONTROL_ACCOUNT_PARAM],
      ControlAccount
    ),
  }
}

function useReport(p: ParsedUrlQuery): useReportRes {
  const lazyData = useLazySearchalyticsGetGeneralLedger(paramsToReq(p))
  const pdf = useLazySearchalyticsGetGeneralLedgerPDF(paramsToReq(p))
  const download = useLazySearchalyticsGetGeneralLedgerCSV(paramsToReq(p))
  return { pdf, download, lazyData }
}

export function generateGeneralLedgerURL(
  location: Location | null,
  timestampRange: TimestampRangePB | null,
  lineItems: LineItem[]
): string {
  return webUrlToString({
    pathname: "/admin/reporting/general-ledger",
    query: deprecatedBuildQueryParams({
      ...defaultStagedParams(GENERAL_LEDGER.config),
      [stagedKey(DATE_RANGE_PARAM)]:
        TimestampRange.toQueryParam(timestampRange),
      [stagedKey(LOCATION_PARAM)]: joinParameters(
        [location].map((l) => UUID.idString(l?.id))
      ),
      [stagedKey(ACCOUNT_PARAM)]: joinParameters(
        lineItems.map((li) => UUID.idString(li.account?.id))
      ),
    }),
  })
}
